import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { ASSET_METADATA, ASSET_TYPE, Asset, AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_METADATA, COST_CENTRE_TYPE, CostCentre, TRANSFER_ASSET_TYPE, TransferAsset, setDefaultLocationFromUser } from 'assets-model';
import { AssetService, BusinessUnitService, CostCentreService, TransferAssetService } from 'assets-service';


@Directive()
export abstract class TransferAssetItemComponentBase extends CrudItemComponent<TransferAsset, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  assetSelectionList: Asset[];

  businessUnitSelectionList: BusinessUnit[];

  costCentreSelectionList: CostCentre[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected transferAssetService: TransferAssetService,
    protected assetService: AssetService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(TRANSFER_ASSET_TYPE, fb, elementRef, router, route, userStorage, dialog, transferAssetService, injector, snackbar);
  }

  createNewItem() {
    return new TransferAsset();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      transferAssetId: [null, []],
      asset: [null, [requiredValidator,]],
      businessUnit: [null, []],
      costCentre: [null, []],
      approvalRejectionComment: [null, []],
      approved: [null, []],
      approvedRejectedBy: [null, []],
      approvedRejectedDate: [null, []],
      assetSrcBusinessUnit: [null, []],
      assetSrcCostCentre: [null, []],
      comments: [null, [requiredValidator,]],
      createdBy: [null, [requiredValidator,]],
      dateCreated: [null, [requiredValidator,]],
      lockVersion: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.transferAssetId?.disable();
    }
    this.assetSrcBusinessUnit?.disable();
    this.assetSrcCostCentre?.disable();
  }
  
  transferAssetIdChanging(value: number, unsetDependents: boolean = true) {}
  
  transferAssetIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.transferAssetIdChangedByUser(value, unsetDependents);
    }
  }  
  
  transferAssetIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  assetDisplay(item: Asset) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSET_TYPE.description(item);
  }
  
  assetLoadSelectionList() {
    this.assetService.findAll().subscribe(
      data => {
        this.assetSelectionList = ASSET_TYPE.entityDecorator.ensureContained(this.item && this.item.asset, data.items);
      }
    );
  }

  assetChanging(value: Asset, unsetDependents: boolean = true) {}
  
  assetChanged(value: Asset, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetChangedByUser(value, unsetDependents);
    }
  }  
  
  assetChangedByUser(value: Asset, unsetDependents: boolean = true) {
  }
  
  assetCompare(a: any, b: any) {
    return ASSET_METADATA.equals(a, b);
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  costCentreDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return COST_CENTRE_TYPE.description(item);
  }
  
  costCentreLoadSelectionList() {
    this.costCentreService.findAll().subscribe(
      data => {
        this.costCentreSelectionList = COST_CENTRE_TYPE.entityDecorator.ensureContained(this.item && this.item.costCentre, data.items);
      }
    );
  }

  costCentreChanging(value: CostCentre, unsetDependents: boolean = true) {}
  
  costCentreChanged(value: CostCentre, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.costCentreChangedByUser(value, unsetDependents);
    }
  }  
  
  costCentreChangedByUser(value: CostCentre, unsetDependents: boolean = true) {
  }
  
  costCentreCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }
  
  approvalRejectionCommentChanging(value: string, unsetDependents: boolean = true) {}
  
  approvalRejectionCommentChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.approvalRejectionCommentChangedByUser(value, unsetDependents);
    }
  }  
  
  approvalRejectionCommentChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  approvedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  approvedChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.approvedChangedByUser(value, unsetDependents);
    }
  }  
  
  approvedChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  approvedRejectedByChanging(value: string, unsetDependents: boolean = true) {}
  
  approvedRejectedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.approvedRejectedByChangedByUser(value, unsetDependents);
    }
  }  
  
  approvedRejectedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  approvedRejectedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  approvedRejectedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.approvedRejectedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  approvedRejectedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  
  
  commentsChanging(value: string, unsetDependents: boolean = true) {}
  
  commentsChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.commentsChangedByUser(value, unsetDependents);
    }
  }  
  
  commentsChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  dateCreatedChanging(value: Date, unsetDependents: boolean = true) {}
  
  dateCreatedChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.dateCreatedChangedByUser(value, unsetDependents);
    }
  }  
  
  dateCreatedChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get transferAssetId() {
    return this.control('transferAssetId');
  }
  
  get asset() {
    return this.control('asset');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get costCentre() {
    return this.control('costCentre');
  }
  
  get approvalRejectionComment() {
    return this.control('approvalRejectionComment');
  }
  
  get approved() {
    return this.control('approved');
  }
  
  get approvedRejectedBy() {
    return this.control('approvedRejectedBy');
  }
  
  get approvedRejectedDate() {
    return this.control('approvedRejectedDate');
  }
  
  get assetSrcBusinessUnit() {
    return this.control('assetSrcBusinessUnit');
  }
  
  get assetSrcCostCentre() {
    return this.control('assetSrcCostCentre');
  }
  
  get comments() {
    return this.control('comments');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get dateCreated() {
    return this.control('dateCreated');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
}





